import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import OperatorLogin from '../operations/OperatorLogin';
import './KeyAccountCheckStation.css';
import './BnCartonCheck.css';

/**
 * B&N carton check — the scan-everything tab on the Carting station.
 *
 * One input takes every barcode on a carton. Scan the (400) PO barcode on the
 * shipping label once per pallet; it picks the build (the server builds one
 * from the PO fax if none exists yet). Then each carton's ship-to (410)
 * barcode, carton code, price sticker and (for IOQ lines) inner pack check off
 * as they are scanned, and a finished carton rolls to the next on the next
 * scan. The server works out what each scan is and checks it against that PO
 * (POST /api/key-account-check/station/quick-scan). Item scans are saved as
 * ordinary checks, so they count toward the Key Account Check Station
 * sign-off.
 */

const UPC_KINDS = [
  { key: 'auto', label: 'Auto' },
  { key: 'sticker', label: 'Price sticker' },
  { key: 'inner', label: 'Inner pack' },
  { key: 'carton', label: 'Carton label' },
];

const VALUE_KINDS = [
  { key: 'auto', label: 'Auto' },
  { key: 'qty', label: 'Quantity' },
  { key: 'weight', label: 'Weight' },
  { key: 'price', label: 'Price' },
];

const EMPTY_CARTON = { po: null, san: null, carton: null, sticker: null, inner: null, qty: null, weight: null, price: null, line: null };

function beep(ok) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const play = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = freq;
      o.type = ok ? 'sine' : 'square';
      g.gain.value = 0.15;
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + start);
      o.stop(ctx.currentTime + start + dur);
    };
    if (ok) play(1200, 0, 0.12);
    else { play(220, 0, 0.25); play(180, 0.3, 0.35); }
    setTimeout(() => ctx.close(), 1000);
  } catch { /* no audio on this device */ }
}

function describe(r) {
  if (r.type === 'po') return `PO ${r.poNumber}`;
  if (r.type === 'san') return 'Ship-to barcode';
  if (r.type === 'sscc') return 'Shipping serial (SSCC)';
  if (r.type === 'item') {
    const what = r.kind === 'carton' ? 'Carton label' : r.kind === 'inner' ? 'Inner pack' : 'Price sticker';
    return r.line ? `${what} · ${r.line.vendorItem}` : what;
  }
  if (r.type === 'value') {
    const what = r.kind === 'qty' ? 'Carton quantity' : r.kind === 'weight' ? 'Carton weight' : r.kind === 'price' ? 'Retail price' : 'Number';
    return r.observed && r.observed.text ? `${what} ${r.observed.text}` : what;
  }
  return 'Unknown barcode';
}

function Step({ label, state, detail }) {
  const cls = state === true ? 'bnc-step-ok' : state === false ? 'bnc-step-bad' : '';
  return (
    <div className={`bnc-step ${cls}`}>
      <div className="bnc-step-mark">{state === true ? 'OK' : state === false ? 'X' : state === 'held' ? '...' : '-'}</div>
      <div>
        <div className="bnc-step-label">{label}</div>
        {state === 'held' && <div className="bnc-step-detail">scanned; checks once the item is scanned</div>}
        {detail && <div className="bnc-step-detail">{detail}</div>}
      </div>
    </div>
  );
}

function lineCounts(counts, poNumber, vendorItem) {
  const c = (counts && counts[`${poNumber}|${vendorItem}`]) || {};
  const sum = (types) => types.reduce((a, t) => ({ pass: a.pass + ((c[t] && c[t].pass) || 0), fail: a.fail + ((c[t] && c[t].fail) || 0) }), { pass: 0, fail: 0 });
  return { carton: sum(['carton_scan', 'carton_photo']), sticker: sum(['sticker_scan', 'sticker_photo']), inner: sum(['inner_scan', 'inner_pack_photo']), qty: sum(['qty_scan']), weight: sum(['weight_scan']), price: sum(['price_scan']) };
}

function isCartonGood(c, act) {
  if (!act || c.po !== true || c.mixed) return false;
  const line = c.line ? act.po.lines.find((l) => l.vendorItem === c.line) : null;
  const needsInner = !!(line && line.needsInnerPacks);
  return c.san !== false && c.carton === true && c.sticker === true && (!needsInner || c.inner === true)
    && c.qty === true && c.weight === true && c.price === true;
}

export default function BnCartonCheck() {
  const [operator, setOperator] = useState(null);
  const [active, setActive] = useState(null); // { runId, poNumber, po }
  const [counts, setCounts] = useState({});
  const [upcKind, setUpcKind] = useState('auto');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [log, setLog] = useState([]);
  const [carton, setCarton] = useState(EMPTY_CARTON);
  const [cartonsDone, setCartonsDone] = useState(0);
  const inputRef = useRef(null);

  // Scans are queued and checked one at a time, so a fast scanner never drops
  // one; refs carry the live carton between queued scans.
  const queue = useRef([]);
  const running = useRef(false);
  const activeRef = useRef(null);
  const cartonRef = useRef(EMPTY_CARTON);
  const operatorRef = useRef(null);
  const kindRef = useRef('auto');
  const [valueKind, setValueKind] = useState('auto');
  const valueKindRef = useRef('auto');
  useEffect(() => { valueKindRef.current = valueKind; }, [valueKind]);
  useEffect(() => { operatorRef.current = operator; }, [operator]);
  useEffect(() => { kindRef.current = upcKind; }, [upcKind]);
  const putCarton = (c) => { cartonRef.current = c; setCarton(c); };

  // Keep the scanner aimed at our field unless someone is typing elsewhere (badge login).
  const refocus = useCallback(() => {
    const el = document.activeElement;
    if (inputRef.current && (!el || el === document.body || el === inputRef.current || el.tagName === 'BUTTON')) inputRef.current.focus();
  }, []);
  useEffect(() => {
    refocus();
    const t = setInterval(refocus, 1500);
    return () => clearInterval(t);
  }, [refocus]);

  // What a 12/13-digit UPC scan is on this carton: the first box still open,
  // price sticker, then inner pack (IOQ lines), then the carton label.
  const autoKind = (c, act) => {
    const line = act && c.line ? act.po.lines.find((l) => l.vendorItem === c.line) : null;
    if (c.sticker !== true) return 'sticker';
    if (line && line.needsInnerPacks && c.inner !== true) return 'inner';
    if (c.carton !== true) return 'carton';
    return 'sticker';
  };
  // A quantity / weight / price scan with no label text fills the first open
  // box in the order the carton label prints them.
  const autoValueKind = (c) => (c.qty !== true ? 'qty' : c.weight !== true ? 'weight' : 'price');

  const checkOne = async (scanned) => {
    let act = activeRef.current;
    let c = cartonRef.current;
    // A finished carton rolls over on the next scan: count it, start a fresh checklist.
    if (act && isCartonGood(c, act)) {
      setCartonsDone((n) => n + 1);
      c = { ...EMPTY_CARTON, po: true };
      putCarton(c);
    }
    const kind = kindRef.current === 'auto' ? autoKind(c, act) : kindRef.current;
    try {
      const res = await axios.post('/api/key-account-check/station/quick-scan', {
        code: scanned, runId: act?.runId, poNumber: act?.poNumber, upcKind: kind, operator: operatorRef.current,
        valueKind: valueKindRef.current === 'auto' ? autoValueKind(c) : valueKindRef.current,
        vendorItem: c.line || undefined,
      });
      const r = res.data;
      // Number scanned before the item on a multi-item PO: hold it, recheck after the item scan.
      if (r.type === 'value' && r.needsItem) {
        beep(true);
        setLast({ ...r, held: true });
        setLog((l) => [{ at: new Date(), raw: scanned, ...r }, ...l].slice(0, 60));
        putCarton({ ...c, [r.kind]: 'held', held: [...(c.held || []), scanned] });
        return;
      }
      beep(r.pass);
      setLast(r);
      setLog((l) => [{ at: new Date(), raw: scanned, ...r }, ...l].slice(0, 60));
      if (r.counts) setCounts(r.counts);

      if (r.type === 'po') {
        if (!act || act.poNumber !== r.poNumber) {
          setCartonsDone(0);
          act = { runId: r.runId, poNumber: r.poNumber, po: r.po };
          activeRef.current = act;
          setActive(act);
          putCarton({ ...EMPTY_CARTON, po: true });
        } else {
          putCarton({ ...c, po: true });
        }
      } else if (r.type === 'san') {
        putCarton({ ...c, san: r.pass });
      } else if (r.type === 'value' && r.kind) {
        putCarton({ ...c, [r.kind]: r.pass, line: c.line || (r.line && r.line.vendorItem) || null });
      } else if (r.type === 'item') {
        // A different item on the same carton is a mix-up, whatever the scan says on its own.
        if (c.line && r.line && c.line !== r.line.vendorItem) {
          putCarton({ ...c, [r.kind]: false, mixed: `${r.line.vendorItem} scanned on a ${c.line} carton` });
        } else {
          putCarton({ ...c, [r.kind]: r.pass, line: c.line || (r.line && r.line.vendorItem) || null, held: [] });
          // Item now known: recheck any held quantity / weight / price scans first.
          if (!c.line && r.line && c.held && c.held.length) queue.current.unshift(...c.held);
        }
      }
    } catch (err) {
      beep(false);
      setLast({ type: 'error', pass: false, reasons: [err.response?.data?.error || err.message] });
    }
  };

  const drain = async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    while (queue.current.length) await checkOne(queue.current.shift());
    running.current = false;
    setBusy(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const submit = (e) => {
    e.preventDefault();
    const scanned = code.trim();
    setCode('');
    if (!scanned) return;
    queue.current.push(scanned);
    drain();
  };

  const nextCarton = () => {
    if (isCartonGood(cartonRef.current, activeRef.current)) setCartonsDone((n) => n + 1);
    putCarton(activeRef.current ? { ...EMPTY_CARTON, po: true } : EMPTY_CARTON);
    setLast(null);
    if (inputRef.current) inputRef.current.focus();
  };

  const newPallet = () => {
    activeRef.current = null;
    setActive(null);
    setCartonsDone(0);
    putCarton(EMPTY_CARTON);
    setLast(null);
    if (inputRef.current) inputRef.current.focus();
  };

  const lineObj = active && carton.line ? active.po.lines.find((l) => l.vendorItem === carton.line) : null;
  const needsInner = !!(lineObj && lineObj.needsInnerPacks);
  const cartonGood = isCartonGood(carton, active);
  const anyBad = [carton.san, carton.carton, carton.sticker, carton.inner, carton.qty, carton.weight, carton.price].includes(false) || !!carton.mixed;

  return (
    <div className="kas-app bnc-app" onClick={refocus}>
      <header className="kas-header">
        <div>
          <h1>B&amp;N carton check</h1>
          <p className="kas-sub">Scan the PO barcode once for the pallet, then every barcode on each carton. Each one checks off as you scan; a finished carton rolls to the next by itself.</p>
        </div>
        <OperatorLogin onOperatorChange={setOperator} department="Key Account Check" />
      </header>

      <section className="kas-card">
        <form onSubmit={submit} className="bnc-scan-form">
          <input
            ref={inputRef}
            className="kas-scan-input bnc-scan-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={active ? `PO ${active.poNumber}: scan the next barcode` : 'Scan the PO barcode on the shipping label to start a pallet'}
            autoComplete="off"
          />
          <button type="submit" className="kas-btn kas-btn-primary" disabled={busy}>Check</button>
        </form>
        <div className="bnc-upc-kind">
          <span>A UPC scan is the:</span>
          {UPC_KINDS.map((k) => (
            <button key={k.key} type="button" className={`kas-mode ${upcKind === k.key ? 'kas-mode-on' : ''}`} onClick={() => setUpcKind(k.key)}>{k.label}</button>
          ))}
          <span className="bnc-upc-note">Auto fills price sticker, then inner pack, then carton label. A 14-digit case code always counts as the carton label.</span>
        </div>
        <div className="bnc-upc-kind">
          <span>A number scan is the:</span>
          {VALUE_KINDS.map((k) => (
            <button key={k.key} type="button" className={`kas-mode ${valueKind === k.key ? 'kas-mode-on' : ''}`} onClick={() => setValueKind(k.key)}>{k.label}</button>
          ))}
          <span className="bnc-upc-note">Auto fills quantity, then weight, then price, unless the barcode says which ($, LBS, QTY).</span>
        </div>
        {!operator && <div className="kas-hint">Scan your badge (top right) so your checks count toward sign-off.</div>}

        {last && (
          <div className={`kas-verdict ${last.pass || last.held ? 'kas-pass' : 'kas-fail'} bnc-verdict`}>
            <div className="kas-verdict-word">{last.held ? 'GOT IT' : last.pass ? 'GOOD' : 'STOP'}</div>
            <div className="bnc-verdict-what">{describe(last)}</div>
            {last.reasons && last.reasons.length > 0 && <ul className="kas-reasons">{last.reasons.map((x, i) => <li key={i}>{x}</li>)}</ul>}
          </div>
        )}
      </section>

      {active && (
        <>
          <section className="kas-card">
            <div className="bnc-carton-head">
              <div className="kas-field-label">This carton</div>
              <div className="bnc-carton-count">Cartons done on this pallet (PO {active.poNumber}): <strong>{cartonsDone}</strong></div>
            </div>
            <div className="bnc-steps">
              <Step label="PO barcode" state={carton.po} detail={`PO ${active.poNumber} · ${active.po?.dc?.city || ''}${active.po?.dc?.state ? `, ${active.po.dc.state}` : ''}`} />
              <Step label="Ship-to barcode" state={carton.san} detail={carton.san == null ? 'optional' : null} />
              <Step label="Carton label" state={carton.carton} detail={carton.line || null} />
              <Step label="Price sticker" state={carton.sticker} detail={lineObj ? `UPC ${lineObj.upc12} · retail $${Number(lineObj.retail).toFixed(2)}` : null} />
              {needsInner && <Step label={`Inner pack (IOQ ${lineObj.ioq})`} state={carton.inner} />}
              <Step label="Carton quantity" state={carton.qty} detail={lineObj ? `should be ${lineObj.casepack}` : null} />
              <Step label="Carton weight" state={carton.weight} />
              <Step label="Retail price" state={carton.price} detail={lineObj ? `should be $${Number(lineObj.retail).toFixed(2)}` : null} />
            </div>
            {carton.mixed && <div className="kas-error">{carton.mixed}</div>}
            <div className="bnc-carton-actions">
              {cartonGood && <div className="bnc-carton-good">Carton is good. Seal it.</div>}
              {anyBad && <div className="bnc-carton-bad">Pull this carton and fix it before it goes on the pallet.</div>}
              <button type="button" className="kas-btn" onClick={nextCarton}>Next carton</button>
              <button type="button" className="kas-btn kas-btn-primary" onClick={newPallet}>New pallet</button>
            </div>
            <div className="kas-hint">PO {active.poNumber} stays selected for the whole pallet. Scanning a carton's shipping label barcodes also checks that label is for this PO; a different PO barcode switches to that PO. New pallet clears the PO and the count.</div>
          </section>

          <section className="kas-card">
            <div className="kas-field-label">PO {active.poNumber} lines</div>
            <table className="kas-feed-table bnc-lines">
              <thead><tr><th>Item</th><th>Title</th><th>UPC</th><th>Casepack</th><th>Cartons</th><th>Carton</th><th>Sticker</th><th>Inner</th><th>Qty</th><th>Weight</th><th>Price</th></tr></thead>
              <tbody>
                {active.po.lines.map((l) => {
                  const c = lineCounts(counts, active.poNumber, l.vendorItem);
                  const cell = (x, needed = true) => (!needed ? <td className="kas-hint">n/a</td> : (
                    <td className={x.fail ? 'kas-bad' : x.pass ? 'kas-ok' : ''}>{x.pass} ok{x.fail ? ` / ${x.fail} bad` : ''}</td>
                  ));
                  return (
                    <tr key={l.vendorItem} className={carton.line === l.vendorItem ? 'bnc-line-on' : ''}>
                      <td><strong>{l.vendorItem}</strong></td>
                      <td>{l.title}</td>
                      <td>{l.upc12}</td>
                      <td>{l.casepack}{l.needsInnerPacks ? ` (IOQ ${l.ioq})` : ''}</td>
                      <td>{l.cartons}</td>
                      {cell(c.carton)}
                      {cell(c.sticker)}
                      {cell(c.inner, l.needsInnerPacks)}
                      {cell(c.qty)}
                      {cell(c.weight)}
                      {cell(c.price)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </>
      )}

      {log.length > 0 && (
        <section className="kas-card">
          <div className="kas-field-label">Scan history</div>
          <table className="kas-feed-table">
            <thead><tr><th>Time</th><th>Scanned</th><th>What</th><th>Result</th></tr></thead>
            <tbody>
              {log.map((x, i) => (
                <tr key={i} className={x.pass ? '' : 'kas-row-fail'}>
                  <td>{x.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                  <td>{x.raw}</td>
                  <td>{describe(x)}</td>
                  <td>{x.pass ? 'good' : (x.reasons || []).join('; ') || 'stop'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
